import { useMemo } from "react";
import { OptimizedBrick, LEGO_BRICK_TYPES } from "../../utils/brickOptimizer";
import { colorPrices, brickTypeMultipliers } from "../../constants/bomPricing";
import { minecraftBlockPrices, minecraftBlockMultipliers, getMinecraftBlockName } from "../../constants/minecraftBlocks";

interface BrickInventory {
  brickType: (typeof LEGO_BRICK_TYPES)[0];
  colorIndex: number;
  colorName: string;
  quantity: number;
  estimatedPrice: number;
}

interface BOMData {
  totalBricks: number;
  totalCost: number;
  brickInventory: BrickInventory[];
  colorSummary: { [key: string]: number };
  layers: Array<{
    layer: number;
    brickCount: number;
    colorDistribution: { [key: string]: number };
  }>;
}

interface LayerData {
  layer: number;
  bricks: OptimizedBrick[];
}

export const useBOMCalculation = (layers: LayerData[], buildingStyle: "lego" | "minecraft" = "lego") => {
  return useMemo((): BOMData => {
    const colorSummary: { [key: string]: number } = {};
    let totalBricks = 0;
    let totalCost = 0;

    // Select pricing based on building style
    const prices = buildingStyle === "minecraft" ? minecraftBlockPrices : colorPrices;
    const multipliers = buildingStyle === "minecraft" ? minecraftBlockMultipliers : brickTypeMultipliers;

    // Aggregate bricks by type and color
    const brickMap = new Map<string, BrickInventory>();

    layers.forEach((layer) => {
      layer.bricks.forEach((brick) => {
        const key = `${brick.colorIndex}_${brick.brickType.id}`;
        const displayName = buildingStyle === "minecraft" 
          ? getMinecraftBlockName(brick.colorName)
          : brick.colorName;

        if (!brickMap.has(key)) {
          brickMap.set(key, {
            brickType: brick.brickType,
            colorIndex: brick.colorIndex,
            colorName: displayName,
            quantity: 0,
            estimatedPrice: 0,
          });
        }

        const inventory = brickMap.get(key)!;
        inventory.quantity += 1;

        // Count colors
        if (!colorSummary[displayName]) {
          colorSummary[displayName] = 0;
        }
        colorSummary[displayName] += 1;

        totalBricks++;
      });
    });

    // Calculate costs
    brickMap.forEach((inventory) => {
      const basePrice = prices[inventory.colorIndex] || (buildingStyle === "minecraft" ? 0.05 : 0.15);
      const typeMultiplier = multipliers[inventory.brickType.id] || 1.0;
      inventory.estimatedPrice = basePrice * typeMultiplier;
      totalCost += inventory.estimatedPrice * inventory.quantity;
    });

    return {
      totalBricks,
      totalCost,
      brickInventory: Array.from(brickMap.values()).sort((a, b) => {
        // Sort by quantity descending, then by type
        if (b.quantity !== a.quantity) return b.quantity - a.quantity;
        return a.brickType.name.localeCompare(b.brickType.name);
      }),
      colorSummary,
      layers: layers.map((layer) => ({
        layer: layer.layer,
        brickCount: layer.bricks.length,
        colorDistribution: layer.bricks.reduce(
          (acc, brick) => {
            acc[brick.colorName] = (acc[brick.colorName] || 0) + 1;
            return acc;
          },
          {} as { [key: string]: number },
        ),
      })),
    };
  }, [layers, buildingStyle]);
};
